import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import { jsPDF } from 'jspdf';
import { todayEastern } from '../dateUtils';

export default function ClientIntakePage() {
  // State management
  const [token, setToken] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  // Form fields
  const [clientName, setClientName] = useState('');
  // Replace age with date of birth
  const [clientDob, setClientDob] = useState('');
  const [isMinor, setIsMinor] = useState(false);
  const [guardianName, setGuardianName] = useState('');
  const [reasonForVisit, setReasonForVisit] = useState('');
  const [initials, setInitials] = useState('');
  const [consentDate, setConsentDate] = useState('');
  const [cancelDate, setCancelDate] = useState('');
  
  // Additional client information fields
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [medications, setMedications] = useState('');
  
  // Emergency contact fields
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhone, setEmergencyContactPhone] = useState('');

  // Workaround: Use state to store signature data instead of direct canvas access
  const [consentSignatureData, setConsentSignatureData] = useState(null);
  const [cancelSignatureData, setCancelSignatureData] = useState(null);
  const [finalSignatureData, setFinalSignatureData] = useState(null);

  // Payment fields
  const [paymentMethod, setPaymentMethod] = useState(''); // 'card' | 'already_provided' | 'cash_check'
  const [cardholderFirst, setCardholderFirst] = useState('');
  const [cardholderLast, setCardholderLast] = useState('');
  const [cardNote, setCardNote] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvv, setCvv] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  
  // Signature refs
  const consentSigRef = useRef(null);
  const cancelSigRef = useRef(null);
  const finalSigRef = useRef(null);
  
  // Form navigation
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5; // Client Info, Consent Forms, Provisions, Payment, Review & Submit

  // Check if client is a minor based on DOB
  useEffect(() => {
    if (clientDob) {
      const today = new Date();
      const birthDate = new Date(clientDob);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      // If birthday hasn't occurred this year yet, subtract a year from age
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      setIsMinor(age < 18);
    } else {
      setIsMinor(false);
    }
  }, [clientDob]);

  // Token validation
  useEffect(() => {
    // Skip validation in development
    if (window.location.hostname === 'localhost') {
      setIsValid(true);
      setLoading(false);
      return;
    }
    
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setErrorMsg('No token provided');
      setLoading(false);
      return;
    }
    
    setToken(t);
    
    // Validate token with API
    axios.get(`/api/forms/intake/validate?token=${t}`)
      .then(res => {
        setIsValid(res.data.success);
        if (!res.data.success) {
          setErrorMsg(res.data.error || 'Invalid link');
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Validation error:', error);
        setErrorMsg('Error validating link');
        setLoading(false);
      });
  }, []);

  // Set default dates to today
  useEffect(() => {
    const today = todayEastern();
    setConsentDate(today);
    setCancelDate(today);
  }, []);

  // Capture signature data when the canvas changes
  const updateConsentSignature = () => {
    try {
      if (consentSigRef.current && !consentSigRef.current.isEmpty()) {
        setConsentSignatureData(consentSigRef.current.toDataURL());
      } else {
        setConsentSignatureData(null);
      }
    } catch (error) {
      console.error('Error updating consent signature:', error);
    }
  };

  const updateCancelSignature = () => {
    try {
      if (cancelSigRef.current && !cancelSigRef.current.isEmpty()) {
        setCancelSignatureData(cancelSigRef.current.toDataURL());
      } else {
        setCancelSignatureData(null);
      }
    } catch (error) {
      console.error('Error updating cancel signature:', error);
    }
  };

  const updateFinalSignature = () => {
    try {
      if (finalSigRef.current && !finalSigRef.current.isEmpty()) {
        setFinalSignatureData(finalSigRef.current.toDataURL());
      } else {
        setFinalSignatureData(null);
      }
    } catch (error) {
      console.error('Error updating final signature:', error);
    }
  };

  // Signature handling
  const clearConsentSig = () => {
    try {
      if (consentSigRef.current) {
        consentSigRef.current.clear();
        setConsentSignatureData(null);
      }
    } catch (error) {
      console.error('Error clearing consent signature:', error);
    }
  };
  
  const clearCancelSig = () => {
    try {
      if (cancelSigRef.current) {
        cancelSigRef.current.clear();
        setCancelSignatureData(null);
      }
    } catch (error) {
      console.error('Error clearing cancel signature:', error);
    }
  };
  
  const clearFinalSig = () => {
    try {
      if (finalSigRef.current) {
        finalSigRef.current.clear();
        setFinalSignatureData(null);
      }
    } catch (error) {
      console.error('Error clearing final signature:', error);
    }
  };

  // Calculate age from date of birth for display
  const calculateAge = (dob) => {
    if (!dob) return '';
    
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // If birthday hasn't occurred this year yet, subtract a year from age
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Navigation handlers
  const nextStep = () => {
    // Validate current step before proceeding
    if (currentStep === 1) {
      if (!clientName.trim()) {
        alert('Please enter the client name');
        return;
      }
      
      if (!clientDob) {
        alert('Please enter the client date of birth');
        return;
      }
      
      if (isMinor && !guardianName.trim()) {
        alert('For clients under 18, please provide a guardian name');
        return;
      }
      
      if (!clientPhone.trim() && !clientEmail.trim()) {
        alert('Please provide at least one contact method (phone or email)');
        return;
      }
      
      if (!emergencyContactName.trim() || !emergencyContactPhone.trim()) {
        alert('Please provide emergency contact information');
        return;
      }
    } else if (currentStep === 2) {
      if (!consentSignatureData) {
        alert('Please sign the Consent to Treat section');
        return;
      }
      
      if (!cancelSignatureData) {
        alert('Please sign the Cancellation Policy section');
        return;
      }
      
      if (!initials.trim()) {
        alert('Please initial the Limits of Confidentiality section');
        return;
      }
    } else if (currentStep === 3) {
      if (!finalSignatureData) {
        alert('Please sign the agreement to the provisions');
        return;
      }
    } else if (currentStep === 4) {
      if (!paymentMethod) {
        alert('Please choose a payment option');
        return;
      }
      if (paymentMethod === 'card') {
        if (!cardholderFirst.trim() || !cardholderLast.trim()) {
          alert('Please enter the cardholder name');
          return;
        }
        if (!cardNumber.replace(/\s+/g, '')) {
          alert('Please enter the card number');
          return;
        }
        if (!expMonth || !expYear) {
          alert('Please select the card expiration date');
          return;
        }
        if (!cvv.trim()) {
          alert('Please enter the card security code (CVV)');
          return;
        }
      }
    }

    setCurrentStep(Math.min(currentStep + 1, totalSteps));
  };
  
  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  // Format date for display - convert from YYYY-MM-DD to MM/DD/YYYY
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year}`;
  };

  // Submit form handler
  const handleSubmit = () => {
    // Prepare data for submission
    const intakeData = {
      clientName: clientName.trim(),
      // Send DOB instead of age
      dob: clientDob,
      isMinor,
      guardianName: guardianName.trim(),
      reasonForVisit: reasonForVisit.trim(),
      clientPhone: clientPhone.trim(),
      clientEmail: clientEmail.trim(),
      address: address.trim(),
      city: city.trim(),
      state: state.trim(),
      zipCode: zipCode.trim(),
      emergencyContactName: emergencyContactName.trim(),
      emergencyContactPhone: emergencyContactPhone.trim(),
      medications: medications.trim(),
      initials: initials.trim(),
      consentDate,
      cancelDate,
      // Include signature data directly in the intakeData object
      consentSignatureData,
      cancelSignatureData,
      finalSignatureData,
      // Payment method choice (never includes raw card data unless 'card')
      paymentMethod
    };

    // Card details are only attached for the 'card' option. They are
    // passed straight to Authorize.Net by the server and never stored.
    if (paymentMethod === 'card') {
      intakeData.card = {
        firstName: cardholderFirst.trim(),
        lastName: cardholderLast.trim(),
        companyName: cardNote.trim(),
        cardNumber: cardNumber.replace(/\D/g, ''),
        expirationDate: `${expYear}-${expMonth}`,
        cardCode: cvv.replace(/\D/g, '')
      };
    }

    // Submit to server
    submitFormData(intakeData);
  };

  // Submit data to server
  const submitFormData = (intakeData) => {
    setErrorMsg('');     // Clear any previous errors
    setPaymentError('');
    setSubmitting(true);

    axios.post('/api/forms/intake/submit', {
      token,
      intakeData,
      // No longer separate from intakeData
      consentSigData: intakeData.consentSignatureData,
      cancelSigData: intakeData.cancelSignatureData,
      finalSigData: intakeData.finalSignatureData
    })
    .then(res => {
      setSubmitting(false);
      if (res.data && res.data.success) {
        setSubmittedData(intakeData); // Store the complete data including signatures
        setSubmitted(true);
      } else {
        const errorMessage = (res.data && res.data.error) ? res.data.error : 'Unknown submission error';
        setErrorMsg(errorMessage);
        console.error('Submission returned error:', errorMessage);
      }
    })
    .catch(error => {
      setSubmitting(false);
      // A card failure should not lose the signed forms — bounce the
      // client back to the payment step to fix the card or switch method.
      const data = error.response?.data;
      if (data?.paymentError) {
        setPaymentError(data.paymentError);
        setCurrentStep(4);
        return;
      }
      console.error('Error submitting form:', error);
      setErrorMsg('Server error submitting intake');
    });
  };

  // PDF generation - Using the popup window approach
  const downloadPdf = () => {
    if (!submittedData) return;
    
    // Log the data being used for PDF generation
    console.log('Generating PDF with data:', submittedData);
    console.log('Signature data present:', {
      consent: submittedData.consentSignatureData ? 'Yes' : 'No',
      cancel: submittedData.cancelSignatureData ? 'Yes' : 'No',
      final: submittedData.finalSignatureData ? 'Yes' : 'No'
    });
    
    // Create a new window for printing
    const newWin = window.open('', '_blank', 'width=800,height=900');
    if (!newWin) {
      alert('Failed to open new window for PDF export. Please check your popup blocker settings.');
      return;
    }

    // Calculate age for PDF display
    const clientAge = calculateAge(submittedData.dob);

    // Build the new window's HTML
    newWin.document.write(`
      <html>
      <head>
        <title>Avenues Intake Form</title>
        <style>
          body {
            font-family: 'Times New Roman', Times, serif;
            margin: 20px;
            padding: 20px;
            line-height: 1.5;
          }
          .page {
            border: 1px solid #000;
            padding: 40px;
            max-width: 800px;
            margin: 0 auto;
            position: relative;
          }
          .header {
            text-align: center;
            margin-bottom: 20px;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
          }
          .signature {
            width: 200px;
            height: auto;
          }
          h2, h3 {
            text-align: center;
            margin-bottom: 20px;
          }
          .section {
            margin-top: 40px;
          }
          .date-line {
            display: inline-block;
            margin-left: 40px;
          }
          .client-info {
            margin-top: 20px;
            border-top: 1px solid #ccc;
            padding-top: 20px;
          }
          .subsection {
            margin-top: 30px;
          }
          .indent {
            margin-left: 20px;
          }
        </style>
      </head>
      <body>
        <div class="page">
          <!-- Header Image -->
          <div class="header">
            <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Letterhead" style="max-width: 100%;">
          </div>
          
          <h2>CONSENT TO TREAT</h2>
          <p>
            I, ${submittedData.isMinor ? submittedData.guardianName : submittedData.clientName}, 
            ${submittedData.isMinor ? `give my permission and consent to Avenues Unlimited Counseling Center,
            Inc., to provide psychotherapeutic treatment to ${submittedData.clientName}.` : 
            `give my permission and consent to Avenues Unlimited Counseling Center,
            Inc., to provide me psychotherapeutic treatment to myself.`}
          </p>
          <p>
            While Avenues Unlimited will endeavor to provide standard of care treatment, I fully understand that 
            because of factors beyond Avenues Unlimited's control, particular outcomes cannot be guaranteed. 
            Furthermore, I understand that I/he/she/we may experience emotional strains because of the counseling 
            or therapy, feel worse during the treatment and make life changes which could be difficult.
          </p>
          <p>
            I understand that Avenues Unlimited is not providing emergency services, and I have been informed of whom to call upon in an emergency
            or during such time as treatment from Avenues Unlimited is unavailable.
          </p>
          <p>
            I understand that regular attendance as recommended by Avenues Unlimited will facilitate maximum therapeutic benefits, but that I/we
            am/are free to discontinue treatment at any time. If I decide to do so I will notify Avenues Unlimited
            at least two weeks in advance so that effective planning for continuing care can be implemented.
          </p>

          <div>
            <p>Consent Signature:</p>
            <img src="${submittedData.consentSignatureData}" class="signature" alt="Consent Signature">
            <span class="date-line">Date: ${formatDate(submittedData.consentDate)}</span>
          </div>

          <div class="section">
            <h3>CANCELLATION POLICY</h3>
            <p>
              I understand that there is a 24 hour (or 1 working day) rescheduling policy. 
              A rescheduling charge of the full session fee will result if I do not give notice in this time frame.
            </p>
            
            <p>Cancellation Signature:</p>
            <img src="${submittedData.cancelSignatureData}" class="signature" alt="Cancellation Signature">
            <span class="date-line">Date: ${formatDate(submittedData.cancelDate)}</span>
          </div>

          <div class="section">
            <h3>LIMITS OF CONFIDENTIALITY</h3>
            <p>
              The contents of counseling, intake and/or assessment sessions are confidential. 
              Both verbal information and written records about a client cannot be shared with another party without 
              the written consent of the client or the client's legal guardian, except as required by law. 
              It is the policy of this center not to release any information about a client without a signed 
              release of information, or as otherwise required by law. Noted exceptions are as follows:
            </p>
            
            <div class="subsection">
              <h4>DUTY TO WARN AND PROTECT</h4>
              <p class="indent">
                When a client discloses intentions or a plan to harm another person, Avenues Unlimited is required by law 
                to warn the intended victim and report identifying information to legal authorities. In cases in which 
                the client discloses or implies a plan for suicide, Avenues Unlimited is required to notify legal 
                authorities and make reasonable attempts to notify the family of the client.
              </p>
            </div>
            
            <div class="subsection">
              <h4>ABUSE OF CHILDREN AND VULNERABLE ADULTS</h4>
              <p class="indent">
                If a client discloses or suggests that he or she is abusing a child (or vulnerable adult) or has recently 
                abused a child (or vulnerable adult); or a child (or vulnerable adult) is in danger of abuse, 
                Avenues Unlimited is required to report this information to the appropriate social service and/or legal authorities.
              </p>
            </div>
            
            <div class="subsection">
              <h4>PRENATAL EXPOSURE TO CONTROLLED SUBSTANCES</h4>
              <p class="indent">
                Avenues Unlimited is required by law to report admitted prenatal exposure to controlled substances 
                that are potentially harmful.
              </p>
            </div>
            
            <div class="subsection">
              <h4>IN THE EVENT OF A CLIENT'S DEATH</h4>
              <p class="indent">
                In the event of a client's death, the spouse or parents of a deceased client have a right to access a client's treatment records.
              </p>
            </div>
            
            <div class="subsection">
              <h4>PROFESSIONAL MISCONDUCT</h4>
              <p class="indent">
                Professional misconduct by a health care professional must be reported by other health care professionals. 
                In cases in which a professional or legal disciplinary meeting is being held regarding the health care professional's actions, 
                related records may be released in order to substantiate disciplinary concerns.
              </p>
            </div>
            
            <div class="subsection">
              <h4>COURT ORDERS</h4>
              <p class="indent">
                Health care professionals are required to release records of clients when a court order has been entered.
              </p>
            </div>
            
            <div class="subsection">
              <h4>MINORS/GUARDIANSHIP</h4>
              <p class="indent">
                Parents or legal guardians of non-emancipated minor clients have the right to access the client's records.
              </p>
            </div>
            
            <p>
              I have been informed of the limits of confidentiality. Initials: <strong>${submittedData.initials}</strong>
            </p>
          </div>
          
          <div class="section">
            <h3>OTHER PROVISIONS</h3>
            <p>
              Payment for services is due and payable at the time services are rendered by Avenues Unlimited. 
              In the event that you fail to pay for any such services when rendered, Avenues Unlimited reserves the right 
              to turn delinquent accounts over to collection agencies and/or attorneys without prior notice. 
              Unpaid account balances accrue interest at the rate of 1.5% per month until paid. Further, in the event 
              Avenues Unlimited incurs attorney's fees and/or court costs to collect delinquent account balances, 
              client agrees to pay or reimburse Avenues Unlimited for the same. Avenues Unlimited may report delinquent 
              account balances to credit reporting agencies, and the client's credit report may state the amount owed, 
              time frame and the name of the clinic.
            </p>
            
            <p>
              Avenues Unlimited may furnish insurance companies and other third-party payers information regarding 
              services rendered by Avenues Unlimited to clients. Information which may be furnished by Avenues Unlimited 
              to these parties includes a description of the type of services, date/times of services, diagnosis, 
              treatment plan, description of impairment, progress of therapy, case notes and summaries. 
              Please note that Avenues Unlimited does not accept insurance payments unless otherwise agreed to in 
              writing between Avenues Unlimited and client.
            </p>
            
            <p>
              When couples, groups or families are receiving services, separate files are kept for individuals for 
              information disclosed that is of a confidential nature. This information includes (a) testing results, 
              (b) information given to the mental health professional not in the presence of the other person(s) 
              utilizing services, (c) information received from other sources about the client, (d) diagnosis, 
              (e) treatment plan, (f) individual reports/summaries and (h) information that has been requested to be separate. 
              The material disclosed in conjoint family or couples sessions in which each part discloses such information 
              in each other's presences, is kept in each file in the form of case notes.
            </p>
            
            <p>
              In the event Avenues Unlimited must telephone or e-mail the client for purposes such as appointments, 
              cancellations or reminders or to give or receive other information, efforts are made to preserve confidentiality.
              
              <br><br>
              Phone Contact: ${submittedData.clientPhone || 'Not provided'}
              <br>
              Email Contact: ${submittedData.clientEmail || 'Not provided'}
              <br><br>
              Emergency Contact: ${submittedData.emergencyContactName}, ${submittedData.emergencyContactPhone}
            </p>
          </div>

          <div class="section">
            <h3>AGREEMENT TO PROVISIONS</h3>
            <p>
              By signing below, I acknowledge that I have read, understood, and agree to all the terms 
              and conditions outlined in this consent form. I have been given the opportunity to ask questions
              about the content of this form, and I understand my rights and responsibilities as a client at
              Avenues Unlimited Counseling Center.
            </p>
            
            <p>Signature:</p>
            <img src="${submittedData.finalSignatureData}" class="signature" alt="Final Acknowledgment Signature">
            <span class="date-line">Date: ${formatDate(submittedData.consentDate)}</span>
          </div>

          <div class="client-info">
            <h3>Client Information</h3>
            <p><strong>Client Name:</strong> ${submittedData.clientName}</p>
            <p><strong>Date of Birth:</strong> ${formatDate(submittedData.dob)} (Age: ${clientAge})</p>
            ${submittedData.isMinor ? `<p><strong>Guardian Name:</strong> ${submittedData.guardianName}</p>` : ''}
            ${submittedData.reasonForVisit ? `<p><strong>Reason for Visit:</strong> ${submittedData.reasonForVisit}</p>` : ''}
            ${submittedData.medications ? `<p><strong>Current Medications:</strong><br>${submittedData.medications.replace(/\n/g, '<br>')}</p>` : ''}
            ${submittedData.clientPhone ? `<p><strong>Phone:</strong> ${submittedData.clientPhone}</p>` : ''}
            ${submittedData.clientEmail ? `<p><strong>Email:</strong> ${submittedData.clientEmail}</p>` : ''}
            ${submittedData.address || submittedData.city || submittedData.state || submittedData.zipCode ? 
              `<p><strong>Address:</strong> ${submittedData.address}${submittedData.address && (submittedData.city || submittedData.state || submittedData.zipCode) ? ', ' : ''}
              ${submittedData.city}${submittedData.city && (submittedData.state || submittedData.zipCode) ? ', ' : ''}
              ${submittedData.state} ${submittedData.zipCode}</p>` : ''}
            <p><strong>Emergency Contact:</strong> ${submittedData.emergencyContactName}, ${submittedData.emergencyContactPhone}</p>
          </div>

          <!-- Footer Image -->
          <div class="footer">
            <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer" style="max-width: 100%;">
          </div>
        </div>

        <script>
          window.onload = function() {
            window.focus();
            window.print();
            // Don't close automatically - let user close
          };
        </script>
      </body>
      </html>
    `);
    newWin.document.close();
  };

  // Progress bar
  const ProgressBar = () => (
    <div style={{ marginBottom: '1.5rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>Step {currentStep} of {totalSteps}</span>
      </div>
      <div style={{ 
        height: '10px', 
        backgroundColor: '#eee', 
        borderRadius: '5px', 
        marginTop: '0.5rem',
        position: 'relative'
      }}>
        <div style={{ 
          height: '100%', 
          width: `${(currentStep / totalSteps) * 100}%`, 
          backgroundColor: '#2a5885', 
          borderRadius: '5px'
        }}></div>
      </div>
    </div>
  );

  // Loading and error states
  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (errorMsg) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: 'red' }}>{errorMsg}</p>
        <button 
          onClick={() => window.location.reload()} 
          style={{ 
            marginTop: 20, 
            padding: '10px 20px',
            backgroundColor: '#2a5885',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (!isValid) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <p style={{ color: 'red' }}>Invalid or used link</p>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <h2>Thank you!</h2>
        <p>Your intake form has been submitted.</p>
        <button 
          onClick={downloadPdf} 
          style={{ 
            marginTop: 20, 
            padding: '10px 20px',
            backgroundColor: '#2a5885',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Download Signed Form (PDF)
        </button>
      </div>
    );
  }

  // Main form - Step 1: Client Information
  if (currentStep === 1) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: 20, fontFamily: 'serif' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img 
            src="/images/avenues-logo.png" 
            alt="Avenues Unlimited" 
            style={{ maxHeight: 80 }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        
        <h2 style={{ textAlign: 'center' }}>CLIENT INTAKE FORM</h2>
        <ProgressBar />
        
        <h3>Client Information</h3>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Client Name:
          </label>
          <input
            type="text"
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem' }}
            required
          />
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Date of Birth:
          </label>
          <input
            type="date"
            value={clientDob}
            onChange={(e) => setClientDob(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem' }}
            required
          />
          {clientDob && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#666' }}>
              Age: {calculateAge(clientDob)} years
            </div>
          )}
        </div>
        
        {isMinor && (
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              Guardian Name:
            </label>
            <input
              type="text"
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem' }}
              required
            />
          </div>
        )}
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Reason for coming in (optional):
          </label>
          <textarea
            value={reasonForVisit}
            onChange={(e) => setReasonForVisit(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', minHeight: '100px' }}
          />
        </div>
        
        {/* Add medications section */}
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Current Medications:
          </label>
          <textarea
            value={medications}
            onChange={(e) => setMedications(e.target.value)}
            style={{ 
              width: '100%', 
              padding: '0.5rem', 
              fontSize: '1rem', 
              minHeight: '100px' 
            }}
            placeholder="Please list all current medications, including dosage and frequency (e.g., Lisinopril 10mg, once daily)"
          />
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Phone:
          </label>
          <input
            type="tel"
            value={clientPhone}
            onChange={(e) => setClientPhone(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem' }}
          />
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Email:
          </label>
          <input
            type="email"
            value={clientEmail}
            onChange={(e) => setClientEmail(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem' }}
          />
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem' }}>
            Address:
          </label>
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            style={{ width: '100%', padding: '0.5rem', fontSize: '1rem', marginBottom: '0.5rem' }}
            placeholder="Street Address"
          />
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              style={{ flex: 2, padding: '0.5rem' }}
              placeholder="City"
            />
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value)}
              style={{ flex: 1, padding: '0.5rem' }}
              placeholder="State"
            />
            <input
              type="text"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              style={{ flex: 1, padding: '0.5rem' }}
              placeholder="Zip Code"
            />
          </div>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
            Emergency Contact Information:
          </label>
          <div style={{ marginBottom: '0.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              Name:
            </label>
            <input
              type="text"
              value={emergencyContactName}
              onChange={(e) => setEmergencyContactName(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem' }}
              required
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem' }}>
              Phone:
            </label>
            <input
              type="tel"
              value={emergencyContactPhone}
              onChange={(e) => setEmergencyContactPhone(e.target.value)}
              style={{ width: '100%', padding: '0.5rem', fontSize: '1rem' }}
              required
            />
          </div>
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
          <button 
            onClick={nextStep}
            style={{ 
              padding: '10px 20px',
              backgroundColor: '#2a5885',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Next: Consent Forms
          </button>
        </div>
      </div>
    );
  }
  
  // Step 2: Consent Forms
  if (currentStep === 2) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: 20, fontFamily: 'serif' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img 
            src="/images/avenues-logo.png" 
            alt="Avenues Unlimited" 
            style={{ maxHeight: 80 }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        
        <h2 style={{ textAlign: 'center' }}>CONSENT FORMS</h2>
        <ProgressBar />
        
        <h3 style={{ textAlign: 'center' }}>CONSENT TO TREAT</h3>
        <p>
          I, <span style={{ fontWeight: 'bold' }}>{isMinor ? guardianName || '_______________' : clientName || '_______________'}</span>, 
          {isMinor 
            ? ` give my permission and consent to Avenues Unlimited Counseling Center, Inc., to provide psychotherapeutic treatment to ${clientName || '_______________'}.` 
            : ' give my permission and consent to Avenues Unlimited Counseling Center, Inc., to provide me psychotherapeutic treatment to myself.'}
        </p>
        <p>
          While Avenues Unlimited will endeavor to provide standard of care treatment, I fully understand that because of factors beyond Avenues Unlimited's control, particular outcomes cannot be guaranteed. Furthermore, I understand that I/he/she/we may experience emotional strains because of the counseling or therapy, feel worse during the treatment and make life changes which could be difficult.
        </p>
        <p>
          I understand that Avenues Unlimited is not providing emergency services, and I have been informed of whom to call upon in an emergency or during such time as treatment from Avenues Unlimited is unavailable.
        </p>
        <p>
          I understand that regular attendance as recommended by Avenues Unlimited will facilitate maximum therapeutic benefits, but that I/we am/are free to discontinue treatment at any time. If I decide to do so I will notify Avenues Unlimited at least two weeks in advance so that effective planning for continuing care can be implemented.
        </p>

        <div style={{ marginTop: 20 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>Consent Signature:</label>
          <div style={{ border: '1px solid #aaa', borderRadius: 4, width: 300, height: 100, backgroundColor: '#fafafa' }}>
            <SignatureCanvas 
              ref={consentSigRef} 
              penColor="black" 
              canvasProps={{ 
                width: 300, 
                height: 100, 
                style: { display: 'block' } 
              }} 
              onEnd={updateConsentSignature}
            />
          </div>
          <div style={{ marginTop: 4 }}>
            <button 
              type="button"
              onClick={clearConsentSig} 
              style={{ 
                marginRight: 12,
                padding: '4px 10px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: '3px'
              }}
            >
              Clear
            </button>
            <label>Date: <input 
              type="date" 
              value={consentDate} 
              onChange={(e) => setConsentDate(e.target.value)} 
            /></label>
          </div>
        </div>

        <h3 style={{ marginTop: 40, textAlign: 'center' }}>CANCELLATION POLICY</h3>
        <p>I understand that there is a 24 hour (or 1 working day) rescheduling policy. A rescheduling charge of the full session fee will result if I do not give notice in this time frame.</p>
        
        <div style={{ marginTop: 20 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>Cancellation Signature:</label>
          <div style={{ border: '1px solid #aaa', borderRadius: 4, width: 300, height: 100, backgroundColor: '#fafafa' }}>
            <SignatureCanvas 
              ref={cancelSigRef} 
              penColor="black" 
              canvasProps={{ 
                width: 300, 
                height: 100, 
                style: { display: 'block' } 
              }} 
              onEnd={updateCancelSignature}
            />
          </div>
          <div style={{ marginTop: 4 }}>
            <button 
              type="button"
              onClick={clearCancelSig} 
              style={{ 
                marginRight: 12,
                padding: '4px 10px',
                backgroundColor: '#f0f0f0',
                border: '1px solid #ccc',
                borderRadius: '3px'
              }}
            >
              Clear
            </button>
            <label>Date: <input 
              type="date" 
              value={cancelDate} 
              onChange={(e) => setCancelDate(e.target.value)} 
            /></label>
          </div>
        </div>

        <h3 style={{ marginTop: 40, textAlign: 'center' }}>LIMITS OF CONFIDENTIALITY</h3>
        <p>
          The contents of counseling, intake and/or assessment sessions are confidential. Both verbal information and written records about a client cannot be shared with another party without the written consent of the client or the client's legal guardian, except as required by law. It is the policy of this center not to release any information about a client without a signed release of information, or as otherwise required by law. Noted exceptions are as follows:
        </p>
        
        <div style={{ marginLeft: 20, marginTop: 15 }}>
          <h4 style={{ margin: '10px 0 5px 0' }}>DUTY TO WARN AND PROTECT</h4>
          <p>
            When a client discloses intentions or a plan to harm another person, Avenues Unlimited is required by law to warn the intended victim and report identifying information to legal authorities. In cases in which the client discloses or implies a plan for suicide, Avenues Unlimited is required to notify legal authorities and make reasonable attempts to notify the family of the client.
          </p>
          
          <h4 style={{ margin: '15px 0 5px 0' }}>ABUSE OF CHILDREN AND VULNERABLE ADULTS</h4>
          <p>
            If a client discloses or suggests that he or she is abusing a child (or vulnerable adult) or has recently abused a child (or vulnerable adult); or a child (or vulnerable adult) is in danger of abuse, Avenues Unlimited is required to report this information to the appropriate social service and/or legal authorities.
          </p>
          
          <h4 style={{ margin: '15px 0 5px 0' }}>PRENATAL EXPOSURE TO CONTROLLED SUBSTANCES</h4>
          <p>
            Avenues Unlimited is required by law to report admitted prenatal exposure to controlled substances that are potentially harmful.
          </p>
          
          <h4 style={{ margin: '15px 0 5px 0' }}>IN THE EVENT OF A CLIENT'S DEATH</h4>
          <p>
            In the event of a client's death, the spouse or parents of a deceased client have a right to access a client's treatment records.
          </p>
          
          <h4 style={{ margin: '15px 0 5px 0' }}>PROFESSIONAL MISCONDUCT</h4>
          <p>
            Professional misconduct by a health care professional must be reported by other health care professionals. In cases in which a professional or legal disciplinary meeting is being held regarding the health care professional's actions, related records may be released in order to substantiate disciplinary concerns.
          </p>
          
          <h4 style={{ margin: '15px 0 5px 0' }}>COURT ORDERS</h4>
          <p>
            Health care professionals are required to release records of clients when a court order has been entered.
          </p>
          
          <h4 style={{ margin: '15px 0 5px 0' }}>MINORS/GUARDIANSHIP</h4>
          <p>
            Parents or legal guardians of non-emancipated minor clients have the right to access the client's records.
          </p>
        </div>
        
        <p style={{ marginTop: 20 }}>
          I have been informed of the limits of confidentiality. 
          <span style={{ 
            backgroundColor: '#FFFF00', 
            padding: '0 5px', 
            marginLeft: '5px' 
          }}>
            Initials: <input 
              type="text" 
              value={initials} 
              onChange={(e) => setInitials(e.target.value)} 
              style={{ 
                border: 'none', 
                borderBottom: '2px solid #000', 
                width: 60, 
                textAlign: 'center',
                backgroundColor: 'transparent'
              }} 
              maxLength={3}
            />
          </span>
        </p>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button 
            onClick={prevStep}
            style={{ 
              padding: '10px 20px',
              backgroundColor: '#4a4a4a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back: Client Information
          </button>
          <button 
            onClick={nextStep}
            style={{ 
              padding: '10px 20px',
              backgroundColor: '#2a5885',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Next: Other Provisions
          </button>
        </div>
      </div>
    );
  }
  
  // Step 3: Other Provisions
  if (currentStep === 3) {
    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: 20, fontFamily: 'serif' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img 
            src="/images/avenues-logo.png" 
            alt="Avenues Unlimited" 
            style={{ maxHeight: 80 }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        
        <h2 style={{ textAlign: 'center' }}>OTHER PROVISIONS</h2>
        <ProgressBar />
        
        <div style={{ border: '1px solid #ccc', padding: 20, marginBottom: 30 }}>
          <p>
            Payment for services is due and payable at the time services are rendered by Avenues Unlimited. 
            In the event that you fail to pay for any such services when rendered, Avenues Unlimited reserves the right 
            to turn delinquent accounts over to collection agencies and/or attorneys without prior notice. 
            Unpaid account balances accrue interest at the rate of 1.5% per month until paid. Further, in the event 
            Avenues Unlimited incurs attorney's fees and/or court costs to collect delinquent account balances, 
            client agrees to pay or reimburse Avenues Unlimited for the same. Avenues Unlimited may report delinquent 
            account balances to credit reporting agencies, and the client's credit report may state the amount owed, 
            time frame and the name of the clinic.
          </p>
          
          <p style={{ marginTop: 15 }}>
            Avenues Unlimited may furnish insurance companies and other third-party payers information regarding 
            services rendered by Avenues Unlimited to clients. Information which may be furnished by Avenues Unlimited 
            to these parties includes a description of the type of services, date/times of services, diagnosis, 
            treatment plan, description of impairment, progress of therapy, case notes and summaries. 
            Please note that Avenues Unlimited does not accept insurance payments unless otherwise agreed to in 
            writing between Avenues Unlimited and client.
          </p>
          
          <p style={{ marginTop: 15 }}>
            When couples, groups or families are receiving services, separate files are kept for individuals for 
            information disclosed that is of a confidential nature. This information includes (a) testing results, 
            (b) information given to the mental health professional not in the presence of the other person(s) 
            utilizing services, (c) information received from other sources about the client, (d) diagnosis, 
            (e) treatment plan, (f) individual reports/summaries and (h) information that has been requested to be separate. 
            The material disclosed in conjoint family or couples sessions in which each part discloses such information 
            in each other's presences, is kept in each file in the form of case notes.
          </p>
          
          <p style={{ marginTop: 15 }}>
            In the event Avenues Unlimited must telephone or e-mail the client for purposes such as appointments, 
            cancellations or reminders or to give or receive other information, efforts are made to preserve confidentiality.
            If we need to reach you by phone, we will ask to speak to you without identifying the name of the clinic. 
            If the person answering asks for more identifying information, we will say this is a personal call.
            If we reach an answering machine or voicemail, we will follow the same guidelines.
          </p>
        </div>
        
        <div style={{ border: '1px solid #ccc', padding: '1.5rem', marginBottom: '2rem', marginTop: '2rem' }}>
          <h3 style={{ textAlign: 'center', marginTop: 0 }}>I AGREE TO THESE PROVISIONS</h3>
          
          <div style={{ marginTop: '2rem' }}>
            <label style={{ display: 'block', marginBottom: 4 }}>Signature:</label>
            <div style={{ border: '1px solid #aaa', borderRadius: 4, width: 300, height: 100, backgroundColor: '#fafafa' }}>
              <SignatureCanvas 
                ref={finalSigRef} 
                penColor="black" 
                canvasProps={{ 
                  width: 300, 
                  height: 100, 
                  style: { display: 'block' } 
                }} 
                onEnd={updateFinalSignature}
              />
            </div>
            <div style={{ marginTop: 4 }}>
              <button 
                type="button"
                onClick={clearFinalSig} 
                style={{ 
                  marginRight: 12,
                  padding: '4px 10px',
                  backgroundColor: '#f0f0f0',
                  border: '1px solid #ccc',
                  borderRadius: '3px'
                }}
              >
                Clear
              </button>
              <label>Date: {formatDate(consentDate)}</label>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button 
            onClick={prevStep}
            style={{ 
              padding: '10px 20px',
              backgroundColor: '#4a4a4a',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Back: Consent Forms
          </button>
          <button
            onClick={nextStep}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2a5885',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Next: Payment Information
          </button>
        </div>
      </div>
    );
  }

  // Step 4: Payment Information
  if (currentStep === 4) {
    const optionStyle = (selected) => ({
      display: 'block',
      border: selected ? '2px solid #2a5885' : '1px solid #ccc',
      borderRadius: 6,
      padding: '12px 14px',
      marginBottom: 12,
      cursor: 'pointer',
      backgroundColor: selected ? '#eef3f9' : '#fff'
    });
    const months = ['01','02','03','04','05','06','07','08','09','10','11','12'];
    const thisYear = new Date().getFullYear();
    const years = Array.from({ length: 13 }, (_, i) => String(thisYear + i));

    const selectCard = () => {
      setPaymentMethod('card');
      // Pre-fill cardholder name with the client's name (editable)
      if (!cardholderFirst && !cardholderLast && clientName.trim()) {
        const parts = clientName.trim().split(/\s+/);
        setCardholderFirst(parts[0] || '');
        setCardholderLast(parts.slice(1).join(' ') || '');
      }
    };

    return (
      <div style={{ maxWidth: 700, margin: '0 auto', padding: 20, fontFamily: 'serif' }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <img
            src="/images/avenues-logo.png"
            alt="Avenues Unlimited"
            style={{ maxHeight: 80 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>

        <h2 style={{ textAlign: 'center' }}>PAYMENT INFORMATION</h2>
        <ProgressBar />

        <p style={{ color: '#444' }}>
          Payment for services is due at the time of each session. Please choose how you would
          like to handle payment. <strong>Your card will not be charged now</strong> — your
          information is stored securely and only used for future session payments.
        </p>

        <label
          style={optionStyle(paymentMethod === 'card')}
          onClick={selectCard}
        >
          <input
            type="radio"
            name="paymentMethod"
            checked={paymentMethod === 'card'}
            onChange={selectCard}
            style={{ marginRight: 8 }}
          />
          <strong>I'll provide my credit/debit card now</strong>
          <div style={{ fontSize: '0.9rem', color: '#666', marginLeft: 24 }}>
            Securely save a card on file for session payments.
          </div>
        </label>

        {paymentMethod === 'card' && (
          <div style={{ border: '1px solid #ddd', borderRadius: 6, padding: 16, marginBottom: 12, backgroundColor: '#fafafa' }}>
            {paymentError && (
              <div style={{
                backgroundColor: '#fdecea', border: '1px solid #f5c2c0', color: '#b71c1c',
                padding: '10px 12px', borderRadius: 4, marginBottom: 12, fontSize: '0.95rem'
              }}>
                {paymentError}
              </div>
            )}
            <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>Cardholder First Name</label>
                <input type="text" value={cardholderFirst} onChange={(e) => setCardholderFirst(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem' }} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>Cardholder Last Name</label>
                <input type="text" value={cardholderLast} onChange={(e) => setCardholderLast(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem' }} />
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', marginBottom: 4 }}>Note (optional, e.g. "Mom's card")</label>
              <input type="text" value={cardNote} onChange={(e) => setCardNote(e.target.value)}
                style={{ width: '100%', padding: '0.5rem' }} />
            </div>

            <div style={{ marginBottom: 10 }}>
              <label style={{ display: 'block', marginBottom: 4 }}>Card Number</label>
              <input type="text" inputMode="numeric" autoComplete="cc-number" value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
                placeholder="•••• •••• •••• ••••"
                style={{ width: '100%', padding: '0.5rem' }} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>Exp. Month</label>
                <select value={expMonth} onChange={(e) => setExpMonth(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem' }}>
                  <option value="">MM</option>
                  {months.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>Exp. Year</label>
                <select value={expYear} onChange={(e) => setExpYear(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem' }}>
                  <option value="">YYYY</option>
                  {years.map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4 }}>CVV</label>
                <input type="text" inputMode="numeric" autoComplete="cc-csc" value={cvv}
                  onChange={(e) => setCvv(e.target.value)} maxLength={4}
                  style={{ width: '100%', padding: '0.5rem' }} />
              </div>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#666', marginTop: 12, marginBottom: 0 }}>
              🔒 Your card will not be charged now. It is stored securely and only used for
              future session payments.
            </p>
          </div>
        )}

        <label
          style={optionStyle(paymentMethod === 'already_provided')}
          onClick={() => setPaymentMethod('already_provided')}
        >
          <input
            type="radio"
            name="paymentMethod"
            checked={paymentMethod === 'already_provided'}
            onChange={() => setPaymentMethod('already_provided')}
            style={{ marginRight: 8 }}
          />
          <strong>I've already given my card information to the office</strong>
        </label>

        <label
          style={optionStyle(paymentMethod === 'cash_check')}
          onClick={() => setPaymentMethod('cash_check')}
        >
          <input
            type="radio"
            name="paymentMethod"
            checked={paymentMethod === 'cash_check'}
            onChange={() => setPaymentMethod('cash_check')}
            style={{ marginRight: 8 }}
          />
          <strong>I'll be paying by cash or check</strong>
        </label>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
          <button
            onClick={prevStep}
            style={{ padding: '10px 20px', backgroundColor: '#4a4a4a', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Back: Other Provisions
          </button>
          <button
            onClick={nextStep}
            style={{ padding: '10px 20px', backgroundColor: '#2a5885', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Next: Review & Submit
          </button>
        </div>
      </div>
    );
  }

  // Step 5: Review & Submit
  return (
    <div style={{ maxWidth: 700, margin: '0 auto', padding: 20, fontFamily: 'serif' }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <img 
          src="/images/avenues-logo.png" 
          alt="Avenues Unlimited" 
          style={{ maxHeight: 80 }}
          onError={(e) => {
            e.target.style.display = 'none';
          }}
        />
      </div>
      
      <h2 style={{ textAlign: 'center' }}>REVIEW & SUBMIT</h2>
      <ProgressBar />
      
      <div style={{ border: '1px solid #ccc', padding: 20, marginBottom: 20, backgroundColor: '#f9f9f9' }}>
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.5rem' }}>Client Information</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '1rem' }}>
          <div><strong>Client Name:</strong></div>
          <div>{clientName}</div>
          
          <div><strong>Date of Birth:</strong></div>
          <div>{formatDate(clientDob)} (Age: {calculateAge(clientDob)})</div>
          
          {isMinor && (
            <>
              <div><strong>Guardian Name:</strong></div>
              <div>{guardianName}</div>
            </>
          )}
          
          {reasonForVisit && (
            <>
              <div><strong>Reason for Visit:</strong></div>
              <div>{reasonForVisit}</div>
            </>
          )}
          
          {medications && (
            <>
              <div><strong>Medications:</strong></div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{medications}</div>
            </>
          )}
          
          <div><strong>Phone:</strong></div>
          <div>{clientPhone}</div>
          
          <div><strong>Email:</strong></div>
          <div>{clientEmail}</div>
        </div>
        
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.5rem' }}>Address</h3>
        <div style={{ marginBottom: '1rem' }}>
          {address && <div>{address}</div>}
          {(city || state || zipCode) && <div>{city}, {state} {zipCode}</div>}
        </div>
        
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.5rem' }}>Emergency Contact</h3>
        <div>
          <div><strong>Name:</strong> {emergencyContactName}</div>
          <div><strong>Phone:</strong> {emergencyContactPhone}</div>
        </div>
      </div>
      
      <div style={{ border: '1px solid #ccc', padding: 20, marginBottom: 20, backgroundColor: '#f9f9f9' }}>
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.5rem' }}>Form Signatures</h3>
        <div style={{ marginBottom: '1rem' }}>
          <div><strong>Consent Signature:</strong> {consentSignatureData ? '✓ Signed' : '✗ Not signed'}</div>
          <div><strong>Consent Date:</strong> {formatDate(consentDate)}</div>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <div><strong>Cancellation Signature:</strong> {cancelSignatureData ? '✓ Signed' : '✗ Not signed'}</div>
          <div><strong>Cancellation Date:</strong> {formatDate(cancelDate)}</div>
        </div>
        
        <div style={{ marginBottom: '1rem' }}>
          <div><strong>Confidentiality Initials:</strong> {initials || '(not provided)'}</div>
        </div>
        
        <div>
          <div><strong>Provisions Signature:</strong> {finalSignatureData ? '✓ Signed' : '✗ Not signed'}</div>
        </div>
      </div>

      <div style={{ border: '1px solid #ccc', padding: 20, marginBottom: 20, backgroundColor: '#f9f9f9' }}>
        <h3 style={{ borderBottom: '1px solid #ddd', paddingBottom: '0.5rem' }}>Payment</h3>
        <div>
          {paymentMethod === 'card' && (
            <div><strong>Method:</strong> Credit/debit card provided {cardNote ? `(${cardNote})` : ''}</div>
          )}
          {paymentMethod === 'already_provided' && (
            <div><strong>Method:</strong> Card information already given to the office</div>
          )}
          {paymentMethod === 'cash_check' && (
            <div><strong>Method:</strong> Paying by cash or check</div>
          )}
          {!paymentMethod && (
            <div style={{ color: '#b71c1c' }}>No payment option selected</div>
          )}
        </div>
      </div>

      <div style={{ marginTop: '2rem', textAlign: 'center' }}>
        <p style={{ fontStyle: 'italic' }}>
          Please review all information above for accuracy before submitting. Once submitted, you will receive a confirmation and can download a copy for your records.
        </p>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
        <button
          onClick={prevStep}
          disabled={submitting}
          style={{
            padding: '10px 20px',
            backgroundColor: '#4a4a4a',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: submitting ? 'not-allowed' : 'pointer'
          }}
        >
          Back: Payment Information
        </button>
        <button
          onClick={handleSubmit}
          disabled={submitting}
          style={{
            padding: '10px 20px',
            backgroundColor: submitting ? '#7d97b0' : '#2a5885',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: submitting ? 'not-allowed' : 'pointer',
            fontWeight: 'bold',
            fontSize: '1.1rem'
          }}
        >
          {submitting ? 'Submitting…' : 'Submit Form'}
        </button>
      </div>
    </div>
  );
}